import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { ActivoFijoLocalService } from 'src/app/services/activo-fijo-local.service';
import { SelectionModel } from '@angular/cdk/collections';

export interface SeleccionActivosDialogData {
  localId: number;
  preSelectedIds?: number[];
}

@Component({
  selector: 'app-seleccion-activos-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTableModule,
    MatTooltipModule,
    FormsModule,
  ],
  template: `
    <h2 mat-dialog-title>Seleccionar Activos Fijos</h2>
    <mat-dialog-content>
      <div class="dialog-header">
        <mat-form-field appearance="outline" class="w-100">
          <mat-label>Buscar activo</mat-label>
          <input matInput [(ngModel)]="searchText" (keyup)="filterActivos()" placeholder="Buscar por código, tipo, marca...">
          <mat-icon matSuffix>search</mat-icon>
        </mat-form-field>

        <div class="actions-bar">
          <button mat-stroked-button color="primary" (click)="selectAll()">
            <mat-icon>select_all</mat-icon> Seleccionar Todos
          </button>
          <button mat-stroked-button (click)="deselectAll()">
            <mat-icon>deselect</mat-icon> Deseleccionar Todos
          </button>
          <span class="counter">
            {{ selection.selected.length }} de {{ activos.length }} seleccionados
          </span>
        </div>
      </div>

      <div *ngIf="isLoading" class="text-center p-3">
        <mat-spinner diameter="40"></mat-spinner>
      </div>

      <div *ngIf="!isLoading && filteredActivos.length === 0" class="text-center p-3">
        No se encontraron activos para este local
      </div>

      <div class="table-container" *ngIf="!isLoading && filteredActivos.length > 0">
        <table mat-table [dataSource]="filteredActivos" class="w-100">

          <ng-container matColumnDef="select">
            <th mat-header-cell *matHeaderCellDef>
              <mat-checkbox
                (change)="toggleAllFiltered($event.checked)"
                [checked]="isAllFilteredSelected()"
                [indeterminate]="isSomeFilteredSelected() && !isAllFilteredSelected()">
              </mat-checkbox>
            </th>
            <td mat-cell *matCellDef="let row">
              <mat-checkbox
                (click)="$event.stopPropagation()"
                (change)="selection.toggle(row)"
                [checked]="selection.isSelected(row)">
              </mat-checkbox>
            </td>
          </ng-container>

          <ng-container matColumnDef="codigo_activo">
            <th mat-header-cell *matHeaderCellDef>Código</th>
            <td mat-cell *matCellDef="let row">{{ row.codigo_activo }}</td>
          </ng-container>

          <ng-container matColumnDef="tipo_equipo">
            <th mat-header-cell *matHeaderCellDef>Tipo Equipo</th>
            <td mat-cell *matCellDef="let row">{{ row.tipo_equipo }}</td>
          </ng-container>

          <ng-container matColumnDef="marca">
            <th mat-header-cell *matHeaderCellDef>Marca</th>
            <td mat-cell *matCellDef="let row">{{ row.marca }}</td>
          </ng-container>

          <ng-container matColumnDef="potencia_equipo">
            <th mat-header-cell *matHeaderCellDef>Potencia</th>
            <td mat-cell *matCellDef="let row">{{ row.potencia_equipo }}</td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns;"
              (click)="selection.toggle(row)"
              [class.selected-row]="selection.isSelected(row)">
          </tr>
        </table>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Cancelar</button>
      <button mat-raised-button
              color="primary"
              [disabled]="selection.selected.length === 0"
              (click)="onConfirm()">
        Confirmar ({{ selection.selected.length }})
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    mat-dialog-content {
      min-height: 300px;
      max-height: 60vh;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }
    .dialog-header {
      flex-shrink: 0;
    }
    .w-100 {
      width: 100%;
    }
    .actions-bar {
      display: flex;
      gap: 8px;
      align-items: center;
      margin-bottom: 12px;
      flex-wrap: wrap;
    }
    .counter {
      margin-left: auto;
      font-size: 14px;
      color: #666;
      font-weight: 500;
    }
    .table-container {
      overflow-y: auto;
      flex: 1;
      min-height: 0;
    }
    .selected-row {
      background-color: #e3f2fd;
    }
    table {
      width: 100%;
    }
    .text-center {
      text-align: center;
    }
    .p-3 {
      padding: 1rem;
    }
  `]
})
export class SeleccionActivosDialogComponent implements OnInit {
  activos: any[] = [];
  filteredActivos: any[] = [];
  isLoading = false;
  searchText = '';
  displayedColumns = ['select', 'codigo_activo', 'tipo_equipo', 'marca', 'potencia_equipo'];
  selection = new SelectionModel<any>(true, []);

  constructor(
    public dialogRef: MatDialogRef<SeleccionActivosDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SeleccionActivosDialogData,
    private activoFijoLocalService: ActivoFijoLocalService
  ) {}

  ngOnInit() {
    this.loadActivos();
  }

  loadActivos() {
    this.isLoading = true;
    this.activoFijoLocalService.getActivosFijosByLocal(this.data.localId).subscribe({
      next: (response: any) => {
        this.activos = response || [];
        this.filteredActivos = [...this.activos];

        // Pre-seleccionar si hay IDs previos
        if (this.data.preSelectedIds?.length) {
          const preSelectedSet = new Set(this.data.preSelectedIds);
          const toSelect = this.activos.filter(a => preSelectedSet.has(a.id));
          this.selection.select(...toSelect);
        }

        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading activos:', error);
        this.isLoading = false;
      }
    });
  }

  filterActivos() {
    if (!this.searchText) {
      this.filteredActivos = [...this.activos];
      return;
    }

    const searchStr = this.searchText.toLowerCase();
    this.filteredActivos = this.activos.filter(activo =>
      (activo.codigo_activo || '').toLowerCase().includes(searchStr) ||
      (activo.tipo_equipo || '').toLowerCase().includes(searchStr) ||
      (activo.marca || '').toLowerCase().includes(searchStr) ||
      (activo.potencia_equipo || '').toLowerCase().includes(searchStr)
    );
  }

  selectAll() {
    this.selection.select(...this.activos);
  }

  deselectAll() {
    this.selection.clear();
  }

  toggleAllFiltered(checked: boolean) {
    if (checked) {
      this.selection.select(...this.filteredActivos);
    } else {
      this.filteredActivos.forEach(row => this.selection.deselect(row));
    }
  }

  isAllFilteredSelected(): boolean {
    return this.filteredActivos.length > 0 &&
      this.filteredActivos.every(row => this.selection.isSelected(row));
  }

  isSomeFilteredSelected(): boolean {
    return this.filteredActivos.some(row => this.selection.isSelected(row));
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onConfirm(): void {
    const selectedIds = this.selection.selected.map(a => a.id);
    this.dialogRef.close(selectedIds);
  }
}
